import { useEffect, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Standard page container - every route inside AppShell renders into one.
 * Provides title (also wired to <title>), optional description, action
 * slot for the top-right CTAs, and a constrained max-width so content
 * stays readable on wide screens.
 */
export interface PageProps {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
  /** Override the max-width container. Defaults to max-w-7xl. */
  width?: 'narrow' | 'default' | 'wide' | 'full'
}

const widthClass = {
  narrow: 'max-w-3xl',
  default: 'max-w-7xl',
  wide: 'max-w-screen-2xl',
  full: 'max-w-none',
} as const

export function Page({
  title,
  description,
  actions,
  children,
  className,
  width = 'default',
}: PageProps) {
  useEffect(() => {
    const previous = document.title
    document.title = title ? `${title} · Bokapsys` : 'Bokapsys'
    return () => {
      document.title = previous
    }
  }, [title])

  const showSubheader = Boolean(description || actions)

  // ───────────────────────────────────────────────────────────────
  // PAGE WRAPPER - NO PADDING, NO CENTERING
  // ───────────────────────────────────────────────────────────────
  // Page-level spacing (gap from sidebar/header) is owned by
  // AppShell <main>. This wrapper does ONLY:
  //   1. max-width (via the `width` prop)
  //   2. title + description + actions header row
  //   3. vertical rhythm between sections (space-y-6)
  //
  // Hard rules - do NOT add to the className of the outer <div>:
  //   • px-* / py-*  → would double-pad on top of AppShell's padding
  //   • mx-auto      → would re-center constrained content and break
  //                    the flush-left alignment with the sidebar
  //
  // Want a different gap? Edit AppShell.tsx <main> ONCE - every page
  // updates together. See CLAUDE.md "Frontend conventions → Page-level
  // spacing" for the rationale.
  return (
    <div className={cn('w-full', widthClass[width], className)}>
      {showSubheader ? (
        // Layout for the description + actions row.
        //
        //   - flex-wrap on the parent so when there are many actions
        //     (e.g. invoice detail page: Back, Download PDF, Resend,
        //     Send reminder, Record payment, Issue credit, Refund,
        //     Write off, Delete draft, Void = 10+ buttons) the actions
        //     block wraps to a new line under the description instead
        //     of overflowing horizontally.
        //   - Inner actions div: min-w-0 + flex-wrap so its own buttons
        //     wrap across multiple rows when the available width is
        //     still tight after the parent wraps. Right-aligned so a
        //     short row still sits flush right.
        //   - No flex-shrink-0 anywhere; we WANT both sides to shrink.
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          {description ? (
            <p className="min-w-0 max-w-2xl text-sm text-muted-foreground">{description}</p>
          ) : (
            <span />
          )}
          {actions ? (
            <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:basis-auto">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-6">{children}</div>
    </div>
  )
}
